import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Users, Ticket as TicketIcon, Megaphone, Tag as TagIcon, FilmStrip, FileText, Truck, ChartBar, Storefront, Envelope, Buildings, MapPin, Package, CheckCircle, X as XIcon, PaperPlaneTilt, Palette } from "@phosphor-icons/react";
import { ImageUploadField, ImageGalleryField } from "@/components/ImageUpload";
import { MarketplaceImportBar } from "@/components/MarketplaceImportBar";
import { useRefreshSiteSettings } from "@/contexts/SiteSettingsContext";
import { PendingDiff } from "@/components/admin/PendingDiff";
import { ShopOrdersTab } from "@/components/admin/ShopOrdersTab";
import { QuotesTab } from "@/components/admin/QuotesTab";

const TABS = [
  { id: "stats", label: "Overview", icon: ChartBar },
  { id: "site_content", label: "Site Content", icon: Palette },
  { id: "members", label: "Members", icon: Users },
  { id: "plans", label: "Plans", icon: TagIcon },
  { id: "tickets", label: "Tickets", icon: TicketIcon },
  { id: "products", label: "Products", icon: TagIcon },
  { id: "shop_orders", label: "Shop Orders", icon: Package },
  { id: "quotes", label: "Quotes", icon: Envelope },
  { id: "videos", label: "Videos", icon: FilmStrip },
  { id: "downloads", label: "Downloads", icon: FileText },
  { id: "inventory", label: "Inventory", icon: Truck },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "listings", label: "Listings Queue", icon: Storefront },
  { id: "blaster_pros", label: "Blaster Pros", icon: Buildings },
  { id: "leads", label: "Leads", icon: Envelope },
  { id: "suppliers", label: "Suppliers", icon: Buildings },
  { id: "pellet_locations", label: "Pellet Locations", icon: MapPin },
  { id: "pellet_orders", label: "Pellet Orders", icon: Package },
];

export default function Admin() {
  const [tab, setTab] = useState("stats");
  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-10">
        <div className="section-label mb-3">— ADMIN CONTROL ROOM</div>
        <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight mb-8" data-testid="admin-headline">Operations Console</h1>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#262626] pb-4" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 font-chivo font-bold text-xs uppercase tracking-wider border ${tab === t.id ? "bg-[#00D4FF] border-[#00D4FF] text-black" : "border-[#262626] text-white hover:border-[#404040]"}`}
            >
              <t.icon size={14} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "stats" && <StatsTab />}
        {tab === "site_content" && <SiteContentTab />}
        {tab === "members" && <MembersTab />}
        {tab === "plans" && <PlansTab />}
        {tab === "tickets" && <TicketsTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "shop_orders" && <ShopOrdersTab />}
        {tab === "quotes" && <QuotesTab />}
        {tab === "videos" && <VideosTab />}
        {tab === "downloads" && <DownloadsTab />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "listings" && <ListingsQueueTab />}
        {tab === "blaster_pros" && <BlasterProsTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "suppliers" && <SuppliersTab />}
        {tab === "pellet_locations" && <PelletLocationsTab />}
        {tab === "pellet_orders" && <PelletOrdersTab />}
      </div>
    </Layout>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((err) => console.error("Failed to load admin stats:", err));
  }, []);
  if (!stats) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  const cards = [
    { label: "Total Members", value: stats.total_members, color: "text-white" },
    { label: "Active Memberships", value: stats.active_members, color: "text-[#00D4FF]" },
    { label: "Open Tickets", value: stats.open_tickets, color: "text-[#FF5722]" },
    { label: "Revenue (Paid)", value: formatMoney(stats.revenue), color: "text-[#00D4FF]" },
    { label: "Expiring (30d)", value: stats.expiring_soon, color: "text-white" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#262626] border border-[#262626]" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-[#0A0A0A] p-6">
          <div className="font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase mb-3">{c.label}</div>
          <div className={`font-mono font-bold text-3xl lg:text-4xl ${c.color}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function MembersTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/members");
      setMembers(data);
    } catch (err) {
      console.error("Failed to load members:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/members/${id}`, { status }); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const updateLevel = async (id, level) => {
    try { await api.patch(`/admin/members/${id}`, { level: Number(level) }); toast.success("Level updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const extend = async (id, days) => {
    try { await api.post(`/admin/members/${id}/extend?days=${days}`); toast.success(`Extended ${days} days`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  return (
    <div className="surface overflow-x-auto" data-testid="admin-members-table">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A]">
          <tr className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-widest">
            <th className="text-left p-4">Name / Email</th>
            <th className="text-left p-4">Company</th>
            <th className="text-left p-4">Level</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Expires</th>
            <th className="text-left p-4">Auto-Renew</th>
            <th className="text-right p-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-t border-[#262626]" data-testid={`member-row-${m.id}`}>
              <td className="p-4">
                <div className="font-chivo font-bold text-white">{m.name}</div>
                <div className="font-mono text-xs text-[#52525B]">{m.email}</div>
              </td>
              <td className="p-4 text-[#9CA3AF]">{m.company || "—"}</td>
              <td className="p-4">
                <select value={m.level ?? 0} onChange={(e) => updateLevel(m.id, e.target.value)} className="input-rugged text-xs py-1" data-testid={`level-select-${m.id}`}>
                  <option value={0}>0 · None</option>
                  <option value={1}>1 · Member</option>
                  <option value={2}>2 · Network Pro</option>
                </select>
              </td>
              <td className="p-4">
                <select value={m.membership_status || "inactive"} onChange={(e) => updateStatus(m.id, e.target.value)} className="input-rugged text-xs py-1" data-testid={`status-select-${m.id}`}>
                  <option value="inactive">Inactive</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </td>
              <td className="p-4 font-mono text-xs text-[#9CA3AF]">{m.membership_expires_at ? formatDate(m.membership_expires_at) : "—"}</td>
              <td className="p-4 font-mono text-xs">{m.auto_renew ? <span className="text-[#00D4FF]">ON</span> : <span className="text-[#52525B]">OFF</span>}</td>
              <td className="p-4 text-right space-x-2">
                <button onClick={() => extend(m.id, 365)} className="tag tag-active hover:bg-[#121212]" data-testid={`extend-1y-${m.id}`}>+1Y</button>
                <button onClick={() => extend(m.id, 30)} className="tag hover:border-[#404040]" data-testid={`extend-30d-${m.id}`}>+30D</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  useEffect(() => {
    api.get("/tickets")
      .then((r) => setTickets(r.data))
      .catch((err) => console.error("Failed to load admin tickets:", err));
  }, []);
  return (
    <div className="surface divide-y divide-[#262626]" data-testid="admin-tickets">
      {tickets.length === 0 && <div className="p-12 text-center font-mono text-xs text-[#52525B]">No tickets.</div>}
      {tickets.map((t) => (
        <a href={`/support/${t.id}`} key={t.id} className="block p-5 hover:bg-[#121212]" data-testid={`admin-ticket-${t.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`tag ${t.status === "open" ? "tag-active" : ""}`}>{t.status}</span>
                <span className="tag">{t.priority}</span>
                <span className="font-mono text-xs text-[#52525B]">{t.category}</span>
              </div>
              <div className="font-chivo font-bold text-white">{t.subject}</div>
              <div className="font-mono text-xs text-[#52525B] mt-1">{t.user_name} · {t.user_email}</div>
            </div>
            <div className="font-mono text-xs text-[#52525B]">{formatDate(t.updated_at)}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

function CrudTab({ resource, fields, testIdPrefix, marketplaceImport = false }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/${resource}`);
      setItems(data);
    } catch (err) {
      console.error(`Failed to load ${resource}:`, err);
    }
  };
  useEffect(() => { load(); }, [resource]);

  const remove = async (id) => {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(`/${resource}/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async (data) => {
    try {
      if (editing && editing.id) {
        await api.put(`/${resource}/${editing.id}`, data);
      } else {
        await api.post(`/${resource}`, data);
      }
      toast.success("Saved");
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid={`${testIdPrefix}-new-btn`}>
          <Plus size={14} weight="bold" /> New
        </button>
      </div>
      {showForm && (
        <CrudForm
          fields={fields}
          initial={editing || {}}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
          testIdPrefix={testIdPrefix}
          marketplaceImport={marketplaceImport}
        />
      )}
      <div className="surface divide-y divide-[#262626]" data-testid={`${testIdPrefix}-list`}>
        {items.length === 0 && <div className="p-12 text-center font-mono text-xs text-[#52525B]">No items.</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4" data-testid={`${testIdPrefix}-row-${it.id}`}>
            <div className="flex-1 min-w-0 pr-4">
              <div className="font-chivo font-bold text-white truncate">{it.title || it.name}</div>
              <div className="font-mono text-xs text-[#52525B]">{it.category} {it.msrp_price ? `· MSRP ${formatMoney(it.msrp_price)} · Member ${formatMoney(it.member_price)}` : ""}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(it); setShowForm(true); }} className="tag hover:border-[#00D4FF]" data-testid={`${testIdPrefix}-edit-${it.id}`}>
                <PencilSimple size={12} weight="bold" />
              </button>
              <button onClick={() => remove(it.id)} className="tag hover:border-[#EF4444] hover:text-[#EF4444]" data-testid={`${testIdPrefix}-delete-${it.id}`}>
                <Trash size={12} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrudForm({ fields, initial, onCancel, onSave, testIdPrefix, marketplaceImport = false }) {
  const renderInitialValue = (fd) => {
    if (initial[fd.key] !== undefined && initial[fd.key] !== null) return initial[fd.key];
    if (fd.type === "checkbox") return false;
    if (fd.type === "number") return 0;
    if (fd.type === "images") return [];
    return "";
  };
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach((fd) => { f[fd.key] = renderInitialValue(fd); });
    return f;
  });

  const applyImport = (imported) => {
    const next = { ...form };
    fields.forEach((fd) => {
      if (imported[fd.key] !== undefined && imported[fd.key] !== null && imported[fd.key] !== "") {
        next[fd.key] = imported[fd.key];
      }
    });
    setForm(next);
  };
  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((fd) => {
      let v = form[fd.key];
      if (fd.type === "number") v = parseFloat(v) || 0;
      payload[fd.key] = v;
    });
    onSave(payload);
  };
  const renderField = (fd) => {
    const testId = `${testIdPrefix}-field-${fd.key}`;
    const setValue = (v) => setForm({ ...form, [fd.key]: v });
    if (fd.type === "textarea") {
      return <textarea value={form[fd.key]} onChange={(e) => setValue(e.target.value)} rows={3} className="input-rugged resize-none" data-testid={testId} />;
    }
    if (fd.type === "select") {
      return (
        <select value={form[fd.key]} onChange={(e) => setValue(e.target.value)} className="input-rugged" data-testid={testId}>
          {fd.options.map((o) => typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (fd.type === "checkbox") {
      return (
        <label className="flex items-center gap-2 input-rugged cursor-pointer">
          <input type="checkbox" checked={!!form[fd.key]} onChange={(e) => setValue(e.target.checked)} data-testid={testId} />
          <span className="font-plex text-sm">{form[fd.key] ? "Yes" : "No"}</span>
        </label>
      );
    }
    if (fd.type === "image") {
      return <ImageUploadField value={form[fd.key]} onChange={setValue} purpose={fd.purpose || "inventory_image"} testId={testId} />;
    }
    if (fd.type === "images") {
      return <ImageGalleryField value={form[fd.key]} onChange={setValue} purpose={fd.purpose || "inventory_image"} testId={testId} />;
    }
    const inputType = fd.type === "number" ? "number" : "text";
    return <input type={inputType} step="0.01" value={form[fd.key]} onChange={(e) => setValue(e.target.value)} className="input-rugged" data-testid={testId} />;
  };

  return (
    <form onSubmit={submit} className="surface p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`${testIdPrefix}-form`}>
      {marketplaceImport && (
        <MarketplaceImportBar onImport={applyImport} testIdPrefix={`${testIdPrefix}-marketplace-import`} />
      )}
      {fields.map((fd) => (
        <div key={fd.key} className={fd.full ? "md:col-span-2" : ""}>
          <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">{fd.label}</label>
          {renderField(fd)}
        </div>
      ))}
      <div className="md:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" data-testid={`${testIdPrefix}-save`}>Save</button>
      </div>
    </form>
  );
}

const productFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "sku", label: "SKU", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "images", label: "Image Gallery (first is primary)", type: "images", full: true, purpose: "product_image" },
  { key: "youtube_url", label: "YouTube Product / Demo Video URL", type: "text", full: true },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "msrp_price", label: "MSRP Price", type: "number" },
  { key: "member_price", label: "Member Price", type: "number" },
  { key: "in_stock", label: "In Stock", type: "checkbox" },
  { key: "purchase_mode", label: "Purchase Mode", type: "select", options: [{ value: "buy", label: "Buy Now (Stripe checkout)" }, { value: "quote", label: "Request a Quote" }] },
];
function ProductsTab() { return <CrudTab resource="products" fields={productFields} testIdPrefix="products" marketplaceImport />; }

const videoFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "youtube_url", label: "YouTube Embed URL", type: "text", full: true },
  { key: "storage_path", label: "Direct Video URL (storage)", type: "text", full: true },
  { key: "thumbnail_url", label: "Thumbnail URL", type: "text", full: true },
  { key: "duration", label: "Duration", type: "text" },
  { key: "featured", label: "Featured", type: "checkbox" },
  { key: "description", label: "Description", type: "textarea", full: true },
];
function VideosTab() { return <CrudTab resource="videos" fields={videoFields} testIdPrefix="videos" />; }

const downloadFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "file_path", label: "File Path / Storage Key", type: "text" },
  { key: "file_name", label: "File Name", type: "text" },
  { key: "description", label: "Description", type: "textarea", full: true },
];
function DownloadsTab() { return <CrudTab resource="downloads" fields={downloadFields} testIdPrefix="downloads" />; }

const inventoryFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "category", label: "Category (Used/Demo/Refurbished)", type: "text" },
  { key: "condition", label: "Condition", type: "text" },
  { key: "hours", label: "Hours", type: "text" },
  { key: "msrp_price", label: "MSRP", type: "number" },
  { key: "member_price", label: "Member Price", type: "number" },
  { key: "images", label: "Image Gallery (first is primary)", type: "images", full: true, purpose: "inventory_image" },
  { key: "youtube_url", label: "YouTube Walkaround / Demo Video URL", type: "text", full: true },
  { key: "released_at", label: "Public Release Date (ISO)", type: "text", full: true },
  { key: "description", label: "Description", type: "textarea", full: true },
  { key: "sold", label: "Sold", type: "checkbox" },
];
function InventoryTab() { return <CrudTab resource="inventory" fields={inventoryFields} testIdPrefix="inventory" marketplaceImport />; }

const announcementFields = [
  { key: "title", label: "Title", type: "text" },
  { key: "pinned", label: "Pinned", type: "checkbox" },
  { key: "body", label: "Body", type: "textarea", full: true },
];
function AnnouncementsTab() { return <CrudTab resource="announcements" fields={announcementFields} testIdPrefix="announcements" />; }


// ---------------------------------------------------------------------------
// Phase 1 — Plans management
// ---------------------------------------------------------------------------
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data);
    } catch (err) {
      console.error("Failed to load plans:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;

  return (
    <div data-testid="admin-plans">
      {editing && <PlanEditor plan={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="surface p-6" data-testid={`admin-plan-level-${p.level}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-[11px] text-[#00D4FF] tracking-widest uppercase">LEVEL {p.level}</div>
                <div className="font-chivo font-black text-2xl text-white uppercase mt-1">{p.name}</div>
              </div>
              <button onClick={() => setEditing(p)} className="btn-secondary text-xs py-2 px-3" data-testid={`edit-plan-${p.level}`}>EDIT</button>
            </div>
            <div className="font-mono text-sm text-[#9CA3AF] space-y-1">
              <div>Yearly: <span className="text-white">{formatMoney(p.yearly_price)}</span></div>
              {p.founding_price && (
                <div>Founding: <span className="text-[#00D4FF]">{formatMoney(p.founding_price)}</span> · {p.founding_remaining}/{p.founding_slots} slots left</div>
              )}
              <div className="text-xs text-[#52525B]">{p.active ? "ACTIVE" : "INACTIVE"} · {p.features.length} features</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanEditor({ plan, onCancel, onSaved }) {
  const [form, setForm] = useState({
    level: plan.level,
    name: plan.name || "",
    yearly_price: plan.yearly_price || 0,
    monthly_price: plan.monthly_price || "",
    founding_price: plan.founding_price || "",
    founding_slots: plan.founding_slots || 0,
    features_text: (plan.features || []).join("\n"),
    active: plan.active !== false,
    sort_order: plan.sort_order || 0,
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        level: Number(form.level),
        name: form.name,
        yearly_price: parseFloat(form.yearly_price) || 0,
        monthly_price: form.monthly_price === "" ? null : parseFloat(form.monthly_price),
        founding_price: form.founding_price === "" ? null : parseFloat(form.founding_price),
        founding_slots: parseInt(form.founding_slots) || 0,
        features: form.features_text.split("\n").map((s) => s.trim()).filter(Boolean),
        active: !!form.active,
        sort_order: parseInt(form.sort_order) || 0,
      };
      await api.put(`/plans/${plan.id}`, payload);
      toast.success("Plan saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="surface p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`plan-editor-${plan.level}`}>
      <div className="md:col-span-2 flex items-end justify-between gap-4">
        <div>
          <div className="section-label">EDIT PLAN</div>
          <h3 className="font-chivo font-black text-2xl text-white uppercase mt-1">Level {plan.level} — {plan.name}</h3>
        </div>
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Name</label>
        <input value={form.name} onChange={onChange("name")} className="input-rugged" data-testid="plan-name" />
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Sort Order</label>
        <input type="number" value={form.sort_order} onChange={onChange("sort_order")} className="input-rugged" />
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Yearly Price (USD)</label>
        <input type="number" step="0.01" value={form.yearly_price} onChange={onChange("yearly_price")} className="input-rugged" data-testid="plan-yearly" />
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Monthly Price (optional)</label>
        <input type="number" step="0.01" value={form.monthly_price} onChange={onChange("monthly_price")} className="input-rugged" placeholder="leave blank to disable" />
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Founding Price (optional)</label>
        <input type="number" step="0.01" value={form.founding_price} onChange={onChange("founding_price")} className="input-rugged" placeholder="e.g. 1500" data-testid="plan-founding-price" />
      </div>
      <div>
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Founding Slots (0 = none)</label>
        <input type="number" value={form.founding_slots} onChange={onChange("founding_slots")} className="input-rugged" data-testid="plan-founding-slots" />
      </div>
      <div className="md:col-span-2">
        <label className="font-mono text-xs text-[#9CA3AF] tracking-widest uppercase block mb-2">Features (one per line)</label>
        <textarea value={form.features_text} onChange={onChange("features_text")} rows={6} className="input-rugged resize-none font-mono text-sm" data-testid="plan-features" />
      </div>
      <div className="md:col-span-2 flex items-center gap-3">
        <label className="flex items-center gap-2 input-rugged cursor-pointer flex-1">
          <input type="checkbox" checked={!!form.active} onChange={onChange("active")} />
          <span className="font-plex text-sm">{form.active ? "Active" : "Inactive"}</span>
        </label>
      </div>
      <div className="md:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="save-plan">
          {saving ? "Saving..." : "Save Plan"}
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// Phase 3 — Listings Queue (admin approval)
// =============================================================================
function ListingsQueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/listings/pending");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const approve = async (lid) => {
    try { await api.post(`/admin/listings/${lid}/approve`); toast.success("Approved + owner emailed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rejectPending = async (lid) => {
    if (!window.confirm("Clear pending changes for this listing?")) return;
    try { await api.post(`/admin/listings/${lid}/reject-pending`); toast.success("Pending changes cleared"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (lid, active) => {
    try { await api.patch(`/admin/listings/${lid}`, { active }); toast.success(active ? "Activated" : "Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleVerified = async (lid, verified) => {
    try { await api.patch(`/admin/listings/${lid}`, { verified }); toast.success(verified ? "Verified" : "Unverified"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;

  return (
    <div className="space-y-3" data-testid="admin-listings-queue">
      {items.length === 0 ? (
        <div className="surface p-8 text-center font-mono text-xs text-[#52525B]" data-testid="admin-listings-empty">No listings awaiting approval.</div>
      ) : items.map((l) => {
        const pending = l.pending_changes || {};
        const hasPending = Object.keys(pending).length > 0;
        return (
          <div key={l.id} className="surface p-5" data-testid={`admin-listing-${l.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-chivo font-bold text-white truncate">{l.business_name}</h3>
                  {!l.approved && <span className="tag text-[#FF5722] border-[#FF5722]/40" data-testid={`admin-listing-status-${l.id}`}>NEW · NOT APPROVED</span>}
                  {hasPending && <span className="tag" data-testid={`admin-listing-pending-${l.id}`}>PENDING EDITS</span>}
                  {l.verified && <span className="tag tag-active">VERIFIED</span>}
                  {!l.active && <span className="tag">INACTIVE</span>}
                </div>
                <div className="font-mono text-xs text-[#52525B] mt-1">
                  {l.slug ? `/pro/${l.slug}` : "no slug"} · {l.city || "—"}, {l.state || "—"} · owner: {l.owner_user_id ? l.owner_user_id.slice(-6) : "—"}
                </div>
                {hasPending && <PendingDiff listing={l} />}
                <p className="font-plex text-sm text-[#9CA3AF] mt-2 line-clamp-2">{l.about || "No description yet."}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:w-auto">
                <button onClick={() => approve(l.id)} className="btn-primary text-xs" data-testid={`admin-approve-${l.id}`}>
                  <CheckCircle size={12} weight="bold" /> Approve
                </button>
                {hasPending && (
                  <button onClick={() => rejectPending(l.id)} className="btn-secondary text-xs" data-testid={`admin-reject-pending-${l.id}`}>
                    <XIcon size={12} weight="bold" /> Reject edits
                  </button>
                )}
                <button onClick={() => toggleVerified(l.id, !l.verified)} className="btn-secondary text-xs" data-testid={`admin-verify-${l.id}`}>
                  {l.verified ? "Un-verify" : "Verify"}
                </button>
                <button onClick={() => toggleActive(l.id, !l.active)} className="btn-secondary text-xs" data-testid={`admin-active-${l.id}`}>
                  {l.active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Admin — Blaster Pros (full CRUD on contractor listings)
// =============================================================================
const SERVICE_TYPE_OPTIONS = [
  { value: "car", label: "Automotive / car" },
  { value: "industrial", label: "Industrial" },
];
const SERVICE_STYLE_OPTIONS = [
  { value: "mobile", label: "Mobile" },
  { value: "in_shop", label: "In-shop" },
];

const emptyListing = () => ({
  business_name: "",
  slug: "",
  owner_user_id: "",
  about: "",
  city: "",
  state: "",
  zip: "",
  address: "",
  show_address: false,
  lat: "",
  lng: "",
  service_types: [],
  service_style: [],
  service_radius_mi: 50,
  sub_services: [],
  phone: "",
  contact_email: "",
  website: "",
  logo_url: "",
  photos: [],
  active: true,
  approved: true,
  verified: false,
});

function BlasterProsTab() {
  const [items, setItems] = useState(null);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null); // existing id | "new" | null
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [l, m] = await Promise.all([
        api.get("/admin/listings"),
        api.get("/admin/members"),
      ]);
      setItems(l.data);
      setMembers(m.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditingId("new");
    setForm(emptyListing());
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({
      ...emptyListing(),
      ...l,
      owner_user_id: l.owner_user_id || "",
      sub_services: l.sub_services || [],
      service_types: l.service_types || [],
      service_style: l.service_style || [],
      lat: l.lat ?? "",
      lng: l.lng ?? "",
    });
  };

  const cancel = () => { setEditingId(null); setForm(null); };

  const save = async () => {
    if (!form.business_name?.trim()) { toast.error("Business name is required"); return; }
    const payload = {
      ...form,
      lat: form.lat === "" || form.lat === null ? null : Number(form.lat),
      lng: form.lng === "" || form.lng === null ? null : Number(form.lng),
      service_radius_mi: Number(form.service_radius_mi) || 50,
      sub_services: Array.isArray(form.sub_services)
        ? form.sub_services
        : String(form.sub_services || "").split(",").map((s) => s.trim()).filter(Boolean),
      owner_user_id: form.owner_user_id || null,
    };
    setSaving(true);
    try {
      if (editingId === "new") {
        await api.post("/admin/listings", payload);
        toast.success("Blaster pro added");
      } else {
        await api.patch(`/admin/listings/${editingId}`, payload);
        toast.success("Changes saved");
      }
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.business_name}? This also removes its leads. Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/listings/${l.id}`);
      toast.success("Listing deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickToggle = async (l, patch) => {
    try {
      await api.patch(`/admin/listings/${l.id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((l) =>
        (l.business_name || "").toLowerCase().includes(q) ||
        (l.city || "").toLowerCase().includes(q) ||
        (l.state || "").toLowerCase().includes(q) ||
        (l.slug || "").toLowerCase().includes(q) ||
        (l.contact_email || "").toLowerCase().includes(q)
      )
    : items;

  return (
    <div className="space-y-4" data-testid="admin-blaster-pros">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <input
          type="text"
          placeholder="Search name, city, state, slug, email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input flex-1 min-w-[260px]"
          data-testid="admin-pros-search"
        />
        <button onClick={startNew} className="btn-primary text-xs" data-testid="admin-pros-add">
          <Plus size={12} weight="bold" /> Add Blaster Pro
        </button>
      </div>

      {editingId && form && (
        <BlasterProForm
          form={form}
          setForm={setForm}
          members={members}
          isNew={editingId === "new"}
          onCancel={cancel}
          onSave={save}
          saving={saving}
        />
      )}

      {filtered.length === 0 ? (
        <div className="surface p-8 text-center font-mono text-xs text-[#52525B]" data-testid="admin-pros-empty">
          {q ? "No blaster pros match that search." : "No blaster pros yet. Click Add Blaster Pro to create one."}
        </div>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full font-plex text-sm" data-testid="admin-pros-table">
            <thead className="bg-[#121212] font-mono text-[11px] text-[#9CA3AF] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Business</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Owner</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const owner = members.find((m) => m.id === l.owner_user_id);
                return (
                  <tr key={l.id} className="border-t border-[#262626]" data-testid={`admin-pro-row-${l.id}`}>
                    <td className="p-3">
                      <div className="font-chivo font-bold text-white">{l.business_name}</div>
                      <div className="font-mono text-[11px] text-[#52525B]">{l.slug ? `/pro/${l.slug}` : "no slug"}</div>
                    </td>
                    <td className="p-3 text-[#9CA3AF]">{l.city || "—"}, {l.state || "—"}</td>
                    <td className="p-3 font-mono text-xs text-[#9CA3AF]">{owner ? owner.email : (l.owner_user_id ? l.owner_user_id.slice(-6) : "— seed —")}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {l.approved ? <span className="tag tag-active">APPROVED</span> : <span className="tag text-[#FF5722] border-[#FF5722]/40">PENDING</span>}
                        {l.verified && <span className="tag tag-active">VERIFIED</span>}
                        {!l.active && <span className="tag">INACTIVE</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button onClick={() => quickToggle(l, { approved: !l.approved })} className="btn-secondary text-xs" data-testid={`admin-pro-approve-${l.id}`}>
                          {l.approved ? "Unapprove" : "Approve"}
                        </button>
                        <button onClick={() => quickToggle(l, { active: !l.active })} className="btn-secondary text-xs" data-testid={`admin-pro-active-${l.id}`}>
                          {l.active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => startEdit(l)} className="btn-secondary text-xs" data-testid={`admin-pro-edit-${l.id}`}>
                          <PencilSimple size={12} weight="bold" /> Edit
                        </button>
                        <button onClick={() => remove(l)} className="btn-secondary text-xs text-[#FF5722] border-[#FF5722]/40" data-testid={`admin-pro-delete-${l.id}`}>
                          <Trash size={12} weight="bold" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BlasterProForm({ form, setForm, members, isNew, onCancel, onSave, saving }) {
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleArr = (key, value) => {
    const cur = new Set(form[key] || []);
    cur.has(value) ? cur.delete(value) : cur.add(value);
    update({ [key]: Array.from(cur) });
  };
  const subServicesText = Array.isArray(form.sub_services) ? form.sub_services.join(", ") : (form.sub_services || "");

  return (
    <div className="surface p-5 space-y-4 border border-[#00D4FF]/30" data-testid="admin-pro-form">
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-white uppercase tracking-tight">
          {isNew ? "+ New Blaster Pro" : `Editing: ${form.business_name || "(unnamed)"}`}
        </h3>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary text-xs" data-testid="admin-pro-form-cancel">Cancel</button>
          <button onClick={onSave} disabled={saving} className="btn-primary text-xs" data-testid="admin-pro-form-save">
            {saving ? "Saving..." : (isNew ? "Create" : "Save changes")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Business name *">
          <input type="text" className="input" value={form.business_name || ""} onChange={(e) => update({ business_name: e.target.value })} data-testid="admin-pro-field-business_name" />
        </Field>
        <Field label="Slug (URL)" hint="Leave blank to auto-generate from name. /pro/your-slug">
          <input type="text" className="input" value={form.slug || ""} onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="admin-pro-field-slug" />
        </Field>

        <Field label="Owner (contractor account)" hint="Leave blank for a seed listing with no owner.">
          <select className="input" value={form.owner_user_id || ""} onChange={(e) => update({ owner_user_id: e.target.value })} data-testid="admin-pro-field-owner">
            <option value="">— no owner (seed) —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.email}{m.level ? ` · L${m.level}` : ""}</option>
            ))}
          </select>
        </Field>
        <Field label="Contact email (lead delivery)">
          <input type="email" className="input" value={form.contact_email || ""} onChange={(e) => update({ contact_email: e.target.value })} data-testid="admin-pro-field-contact_email" />
        </Field>

        <Field label="Phone"><input type="text" className="input" value={form.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="admin-pro-field-phone" /></Field>
        <Field label="Website"><input type="url" className="input" value={form.website || ""} onChange={(e) => update({ website: e.target.value })} data-testid="admin-pro-field-website" /></Field>

        <Field label="City"><input type="text" className="input" value={form.city || ""} onChange={(e) => update({ city: e.target.value })} data-testid="admin-pro-field-city" /></Field>
        <Field label="State (2-letter)"><input type="text" maxLength={2} className="input uppercase" value={form.state || ""} onChange={(e) => update({ state: e.target.value.toUpperCase() })} data-testid="admin-pro-field-state" /></Field>

        <Field label="ZIP"><input type="text" className="input" value={form.zip || ""} onChange={(e) => update({ zip: e.target.value })} data-testid="admin-pro-field-zip" /></Field>
        <Field label="Address (street)"><input type="text" className="input" value={form.address || ""} onChange={(e) => update({ address: e.target.value })} data-testid="admin-pro-field-address" /></Field>

        <Field label="Latitude" hint="Leave blank to auto-fill from zip"><input type="number" step="any" className="input" value={form.lat ?? ""} onChange={(e) => update({ lat: e.target.value })} data-testid="admin-pro-field-lat" /></Field>
        <Field label="Longitude" hint="Leave blank to auto-fill from zip"><input type="number" step="any" className="input" value={form.lng ?? ""} onChange={(e) => update({ lng: e.target.value })} data-testid="admin-pro-field-lng" /></Field>

        <Field label="Service radius (miles)"><input type="number" min={1} max={500} className="input" value={form.service_radius_mi || 50} onChange={(e) => update({ service_radius_mi: e.target.value })} data-testid="admin-pro-field-radius" /></Field>
        <Field label="Show street address on public page?">
          <label className="flex items-center gap-2 font-plex text-sm text-[#9CA3AF] pt-2">
            <input type="checkbox" checked={!!form.show_address} onChange={(e) => update({ show_address: e.target.checked })} data-testid="admin-pro-field-show_address" />
            Display full street address
          </label>
        </Field>

        <Field label="Service types">
          <div className="flex flex-wrap gap-2 pt-2">
            {SERVICE_TYPE_OPTIONS.map((o) => (
              <button type="button" key={o.value} onClick={() => toggleArr("service_types", o.value)} data-testid={`admin-pro-field-stype-${o.value}`}
                className={`tag ${form.service_types?.includes(o.value) ? "tag-active" : ""}`}>
                {o.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Service style">
          <div className="flex flex-wrap gap-2 pt-2">
            {SERVICE_STYLE_OPTIONS.map((o) => (
              <button type="button" key={o.value} onClick={() => toggleArr("service_style", o.value)} data-testid={`admin-pro-field-sstyle-${o.value}`}
                className={`tag ${form.service_style?.includes(o.value) ? "tag-active" : ""}`}>
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <div className="md:col-span-2">
          <Field label="About / description">
            <textarea rows={4} className="input" value={form.about || ""} onChange={(e) => update({ about: e.target.value })} data-testid="admin-pro-field-about" />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Sub-services (comma separated)" hint="E.g. Automotive restoration, Food & bev lines, Mold remediation">
            <input type="text" className="input" value={subServicesText} onChange={(e) => update({ sub_services: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="admin-pro-field-sub_services" />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Logo">
            <ImageUploadField
              value={form.logo_url || ""}
              onChange={(url) => update({ logo_url: url })}
              purpose="listing_logo"
              testId="admin-pro-field-logo"
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Gallery photos (first is primary)">
            <ImageGalleryField
              value={form.photos || []}
              onChange={(arr) => update({ photos: arr })}
              purpose="listing_photo"
              testId="admin-pro-field-photos"
            />
          </Field>
        </div>

        <Field label="Approved (visible on finder / directory)">
          <label className="flex items-center gap-2 font-plex text-sm text-[#9CA3AF] pt-2">
            <input type="checkbox" checked={!!form.approved} onChange={(e) => update({ approved: e.target.checked })} data-testid="admin-pro-field-approved" /> Approved
          </label>
        </Field>
        <Field label="Verified badge">
          <label className="flex items-center gap-2 font-plex text-sm text-[#9CA3AF] pt-2">
            <input type="checkbox" checked={!!form.verified} onChange={(e) => update({ verified: e.target.checked })} data-testid="admin-pro-field-verified" /> Verified
          </label>
        </Field>
        <Field label="Active">
          <label className="flex items-center gap-2 font-plex text-sm text-[#9CA3AF] pt-2">
            <input type="checkbox" checked={!!form.active} onChange={(e) => update({ active: e.target.checked })} data-testid="admin-pro-field-active" /> Active
          </label>
        </Field>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-widest mb-1">{label}</div>
      {children}
      {hint && <div className="font-mono text-[10px] text-[#52525B] mt-1">{hint}</div>}
    </div>
  );
}



// =============================================================================
// Phase 3 — Leads (admin-wide)
// =============================================================================
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  return (
    <div className="surface overflow-x-auto" data-testid="admin-leads-table">
      <table className="w-full font-plex text-sm">
        <thead className="bg-[#121212] font-mono text-[11px] text-[#9CA3AF] uppercase tracking-widest">
          <tr>
            <th className="text-left p-3">Date</th>
            <th className="text-left p-3">Customer</th>
            <th className="text-left p-3">Contact</th>
            <th className="text-left p-3">Zip</th>
            <th className="text-left p-3">Source</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Routed to</th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 && (
            <tr><td colSpan={7} className="p-6 text-center text-[#52525B] font-mono text-xs">No leads yet.</td></tr>
          )}
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-[#262626]" data-testid={`admin-lead-${l.id}`}>
              <td className="p-3 font-mono text-xs text-[#9CA3AF]">{formatDate(l.created_at)}</td>
              <td className="p-3 text-white">{l.customer_name}</td>
              <td className="p-3 text-[#9CA3AF]">{l.customer_email || "—"}{l.customer_phone ? ` · ${l.customer_phone}` : ""}</td>
              <td className="p-3 font-mono text-xs text-white">{l.zip || "—"}</td>
              <td className="p-3 text-[#9CA3AF]">{l.source || "—"}</td>
              <td className="p-3"><span className="tag">{(l.status || "new").toUpperCase()}</span></td>
              <td className="p-3 font-mono text-xs text-[#9CA3AF]">{l.listing_id ? l.listing_id.slice(-6) : "unmatched"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Phase 4 — Suppliers
// =============================================================================
function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/suppliers"); setItems(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/suppliers/${editing.id}`, form);
      else await api.post(`/admin/suppliers`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) return <SupplierForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />;

  return (
    <div data-testid="admin-suppliers">
      <div className="flex items-center justify-between mb-4">
        <div className="section-label">— SUPPLIERS</div>
        <button onClick={() => setEditing({})} className="btn-primary text-xs" data-testid="admin-supplier-new"><Plus size={12} weight="bold" /> New supplier</button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="surface p-6 text-center font-mono text-xs text-[#52525B]">No suppliers yet.</div>}
        {items.map((s) => (
          <div key={s.id} className="surface p-4 flex items-center justify-between gap-3" data-testid={`admin-supplier-${s.id}`}>
            <div className="min-w-0">
              <div className="font-chivo font-bold text-white truncate">{s.name}</div>
              <div className="font-mono text-xs text-[#52525B] truncate">{s.contact_name} · {s.contact_email} · {s.contact_phone}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`tag ${s.active ? "tag-active" : ""}`}>{s.active ? "ACTIVE" : "INACTIVE"}</span>
              <button onClick={() => setEditing(s)} className="btn-secondary text-xs" data-testid={`admin-supplier-edit-${s.id}`}><PencilSimple size={12} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupplierForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: "", contact_name: "", contact_email: "", contact_phone: "",
    billing_info: "", notes: "", active: true, ...initial,
  });
  const [saving, setSaving] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => { e.preventDefault(); setSaving(true); await onSave(form); setSaving(false); };
  return (
    <form onSubmit={submit} className="surface p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-supplier-form">
      <input required placeholder="Supplier name" value={form.name} onChange={onChange("name")} className="input-rugged md:col-span-2" data-testid="admin-supplier-name" />
      <input placeholder="Contact name" value={form.contact_name} onChange={onChange("contact_name")} className="input-rugged" data-testid="admin-supplier-contact-name" />
      <input type="email" placeholder="Contact email" value={form.contact_email} onChange={onChange("contact_email")} className="input-rugged" data-testid="admin-supplier-contact-email" />
      <input placeholder="Contact phone" value={form.contact_phone} onChange={onChange("contact_phone")} className="input-rugged" data-testid="admin-supplier-contact-phone" />
      <input placeholder="Billing info (payout terms)" value={form.billing_info} onChange={onChange("billing_info")} className="input-rugged" data-testid="admin-supplier-billing" />
      <textarea placeholder="Internal notes" rows={3} value={form.notes} onChange={onChange("notes")} className="input-rugged md:col-span-2" />
      <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
        <input type="checkbox" checked={!!form.active} onChange={onChange("active")} /> <span className="font-plex text-sm">{form.active ? "Active" : "Inactive"}</span>
      </label>
      <div className="md:col-span-2 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-supplier-save">{saving ? "Saving..." : "Save"}</button>
      </div>
    </form>
  );
}

// =============================================================================
// Phase 4 — Pellet Locations & Prices
// =============================================================================
function PelletLocationsTab() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [pricesFor, setPricesFor] = useState(null);

  const load = async () => {
    try {
      const [{ data: locs }, { data: sups }] = await Promise.all([
        api.get("/admin/pellet-locations"),
        api.get("/admin/suppliers"),
      ]);
      setItems(locs); setSuppliers(sups);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/pellet-locations/${editing.id}`, form);
      else await api.post(`/admin/pellet-locations`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) return <PelletLocationForm initial={editing} suppliers={suppliers} onSave={save} onCancel={() => setEditing(null)} />;
  if (pricesFor) return <PelletPricesPanel location={pricesFor} onClose={() => { setPricesFor(null); load(); }} />;

  return (
    <div data-testid="admin-pellet-locations">
      <div className="flex items-center justify-between mb-4">
        <div className="section-label">— PELLET LOCATIONS</div>
        <button onClick={() => setEditing({})} className="btn-primary text-xs" data-testid="admin-pellet-location-new"><Plus size={12} weight="bold" /> New location</button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="surface p-6 text-center font-mono text-xs text-[#52525B]">No pellet locations yet.</div>}
        {items.map((l) => {
          const supplier = suppliers.find((s) => s.id === l.supplier_id);
          return (
            <div key={l.id} className="surface p-4 flex items-center justify-between gap-3" data-testid={`admin-pellet-location-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-chivo font-bold text-white truncate">{l.name}</div>
                <div className="font-mono text-xs text-[#52525B] truncate">{l.city}, {l.state} {l.zip} · supplier: {supplier?.name || "—"}</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] mt-1">{l.pickup ? "PICKUP" : ""}{l.pickup && l.delivery ? " · " : ""}{l.delivery ? `DELIVERY (${l.delivery_fee_type} $${l.delivery_fee})` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`tag ${l.active ? "tag-active" : ""}`}>{l.active ? "ACTIVE" : "INACTIVE"}</span>
                <button onClick={() => setPricesFor(l)} className="btn-secondary text-xs" data-testid={`admin-pellet-prices-${l.id}`}>Prices</button>
                <button onClick={() => setEditing(l)} className="btn-secondary text-xs" data-testid={`admin-pellet-location-edit-${l.id}`}><PencilSimple size={12} weight="bold" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PelletLocationForm({ initial, suppliers, onSave, onCancel }) {
  const [form, setForm] = useState({
    supplier_id: "", name: "", address: "", city: "", state: "", zip: "",
    lat: null, lng: null, contact_email: "", contact_phone: "", hours: "",
    lead_time: "24 hours", pickup_window_text: "", pickup: true, delivery: false,
    delivery_radius_mi: 50, delivery_fee_type: "flat", delivery_fee: 0,
    free_delivery_above_lbs: 0, min_order_lbs: 50, max_order_lbs: 2000,
    step_lbs: 50, container_fee: 0, active: true, ...initial,
  });
  const [saving, setSaving] = useState(false);
  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...form, lat: form.lat ? Number(form.lat) : null, lng: form.lng ? Number(form.lng) : null };
    await onSave(payload); setSaving(false);
  };
  return (
    <form onSubmit={submit} className="surface p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-pellet-location-form">
      <select required value={form.supplier_id} onChange={onChange("supplier_id")} className="input-rugged md:col-span-3" data-testid="admin-pellet-location-supplier">
        <option value="">Select supplier</option>
        {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <input required placeholder="Location name" value={form.name} onChange={onChange("name")} className="input-rugged md:col-span-3" data-testid="admin-pellet-location-name" />
      <input placeholder="Street address" value={form.address} onChange={onChange("address")} className="input-rugged md:col-span-2" />
      <input maxLength={5} placeholder="Zip" value={form.zip} onChange={onChange("zip")} className="input-rugged" data-testid="admin-pellet-location-zip" />
      <input placeholder="City" value={form.city} onChange={onChange("city")} className="input-rugged" />
      <input maxLength={2} placeholder="State (2-letter)" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="input-rugged" />
      <input type="email" placeholder="Depot contact email" value={form.contact_email} onChange={onChange("contact_email")} className="input-rugged" />
      <input placeholder="Hours (e.g. Mon-Fri 7a-5p)" value={form.hours} onChange={onChange("hours")} className="input-rugged md:col-span-2" />
      <input placeholder="Lead time (e.g. 24 hours)" value={form.lead_time} onChange={onChange("lead_time")} className="input-rugged" />
      <label className="flex items-center gap-2 input-rugged cursor-pointer"><input type="checkbox" checked={!!form.pickup} onChange={onChange("pickup")} /> Pickup</label>
      <label className="flex items-center gap-2 input-rugged cursor-pointer"><input type="checkbox" checked={!!form.delivery} onChange={onChange("delivery")} /> Delivery</label>
      <input type="number" placeholder="Delivery radius (mi)" value={form.delivery_radius_mi} onChange={onChange("delivery_radius_mi")} className="input-rugged" />
      <input type="number" placeholder="Delivery fee $" value={form.delivery_fee} onChange={onChange("delivery_fee")} className="input-rugged" />
      <input type="number" placeholder="Free above lbs" value={form.free_delivery_above_lbs} onChange={onChange("free_delivery_above_lbs")} className="input-rugged" />
      <input type="number" placeholder="Min lbs" value={form.min_order_lbs} onChange={onChange("min_order_lbs")} className="input-rugged" />
      <input type="number" placeholder="Max lbs" value={form.max_order_lbs} onChange={onChange("max_order_lbs")} className="input-rugged" />
      <input type="number" placeholder="Step lbs" value={form.step_lbs} onChange={onChange("step_lbs")} className="input-rugged" />
      <label className="flex items-center gap-2 md:col-span-3 cursor-pointer"><input type="checkbox" checked={!!form.active} onChange={onChange("active")} /> <span className="font-plex text-sm">{form.active ? "Active" : "Inactive"}</span></label>
      <div className="md:col-span-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-pellet-location-save">{saving ? "Saving..." : "Save"}</button>
      </div>
    </form>
  );
}

function PelletPricesPanel({ location, onClose }) {
  const [prices, setPrices] = useState([]);
  const [form, setForm] = useState({ pellet_size: "9mm", msrp_lb: 1.65, cost_lb: 0.80, member_lb: 1.20, stock: "in_stock" });
  const load = async () => {
    try { const { data } = await api.get(`/admin/pellet-prices?location_id=${location.id}`); setPrices(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [location.id]); // eslint-disable-line
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/pellet-prices`, { location_id: location.id, ...form, msrp_lb: Number(form.msrp_lb), cost_lb: Number(form.cost_lb), member_lb: Number(form.member_lb) });
      toast.success("Price saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid={`admin-pellet-prices-panel-${location.id}`}>
      <div className="flex items-center justify-between">
        <div><div className="section-label">PRICES</div><h3 className="font-chivo font-bold text-white">{location.name}</h3></div>
        <button onClick={onClose} className="btn-secondary text-xs" data-testid="admin-pellet-prices-close"><XIcon size={12} weight="bold" /> Back</button>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full font-plex text-sm">
          <thead className="bg-[#121212] font-mono text-[11px] text-[#9CA3AF] uppercase">
            <tr><th className="text-left p-3">Size</th><th className="text-left p-3">MSRP /lb</th><th className="text-left p-3">Cost /lb</th><th className="text-left p-3">Member /lb</th><th className="text-left p-3">Stock</th></tr>
          </thead>
          <tbody>
            {prices.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-[#52525B] text-xs">No prices set yet.</td></tr>}
            {prices.map((p) => (
              <tr key={p.id} className="border-t border-[#262626]">
                <td className="p-3 text-white">{p.pellet_size}</td>
                <td className="p-3">{formatMoney(p.msrp_lb)}</td>
                <td className="p-3 text-[#9CA3AF]">{formatMoney(p.cost_lb)}</td>
                <td className="p-3 text-[#00D4FF]">{formatMoney(p.member_lb)}</td>
                <td className="p-3"><span className="tag">{p.stock}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={save} className="surface p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="admin-pellet-price-form">
        <select value={form.pellet_size} onChange={(e) => setForm({ ...form, pellet_size: e.target.value })} className="input-rugged">
          {["3mm","9mm","16mm","nuggets","blocks"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="number" step="0.01" placeholder="MSRP /lb" value={form.msrp_lb} onChange={(e) => setForm({ ...form, msrp_lb: e.target.value })} className="input-rugged" />
        <input type="number" step="0.01" placeholder="Cost /lb" value={form.cost_lb} onChange={(e) => setForm({ ...form, cost_lb: e.target.value })} className="input-rugged" />
        <input type="number" step="0.01" placeholder="Member /lb" value={form.member_lb} onChange={(e) => setForm({ ...form, member_lb: e.target.value })} className="input-rugged" />
        <select value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-rugged">
          {["in_stock","low","out","preorder"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="submit" className="btn-primary" data-testid="admin-pellet-price-save">Save</button>
      </form>
    </div>
  );
}

// =============================================================================
// Phase 4 — Pellet Orders
// =============================================================================
function PelletOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/admin/pellet-orders"); setOrders(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (oid, status) => {
    try { await api.patch(`/admin/pellet-orders/${oid}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const sendInvoice = async (oid) => {
    if (!window.confirm("Generate Stripe payment link and email it to the member?")) return;
    try {
      const { data } = await api.post(`/admin/pellet-orders/${oid}/send-invoice`);
      toast.success("Invoice sent + payment link emailed");
      window.open(data.payment_url, "_blank");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
  return (
    <div data-testid="admin-pellet-orders">
      <div className="section-label mb-3">— PELLET ORDERS</div>
      <div className="space-y-2">
        {orders.length === 0 && <div className="surface p-6 text-center font-mono text-xs text-[#52525B]">No pellet orders yet.</div>}
        {orders.map((o) => (
          <div key={o.id} className="surface p-4" data-testid={`admin-pellet-order-${o.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-chivo font-bold text-white">#{o.id.slice(-6)}</span>
                  <span className="tag" data-testid={`admin-order-status-${o.id}`}>{(o.status || "").toUpperCase()}</span>
                </div>
                <div className="font-mono text-xs text-[#52525B] mt-1">
                  {formatDate(o.created_at)} · {o.member_email} · {o.pounds} lbs {o.pellet_size} · {o.delivery_method} · pickup {o.pickup_date}
                </div>
                <div className="font-mono text-xs text-[#9CA3AF] mt-1">
                  {o.location_name} · subtotal <span className="text-white">{formatMoney(o.pellet_subtotal)}</span> · delivery <span className="text-white">{formatMoney(o.delivery_fee)}</span> · <strong className="text-[#00D4FF]">Total {formatMoney(o.estimated_total)}</strong>
                </div>
                {o.notes && <div className="font-plex text-xs text-[#9CA3AF] mt-1 italic">Notes: {o.notes}</div>}
                {o.stripe_payment_url && (
                  <div className="font-mono text-[10px] text-[#52525B] mt-1 break-all">Payment URL: <a className="text-[#00D4FF]" href={o.stripe_payment_url} target="_blank" rel="noreferrer">{o.stripe_payment_url.slice(0, 60)}...</a></div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {o.status === "awaiting_supplier" && (
                  <button onClick={() => updateStatus(o.id, "confirmed")} className="btn-secondary text-xs" data-testid={`admin-order-confirm-${o.id}`}>
                    <CheckCircle size={12} weight="bold" /> Confirm
                  </button>
                )}
                {(o.status === "confirmed" || o.status === "awaiting_supplier") && (
                  <button onClick={() => sendInvoice(o.id)} className="btn-primary text-xs" data-testid={`admin-order-invoice-${o.id}`}>
                    <PaperPlaneTilt size={12} weight="bold" /> Send invoice
                  </button>
                )}
                {o.status !== "paid" && o.status !== "cancelled" && (
                  <button onClick={() => updateStatus(o.id, "cancelled")} className="btn-secondary text-xs" data-testid={`admin-order-cancel-${o.id}`}>
                    <XIcon size={12} weight="bold" /> Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// =============================================================================
// Phase 7 — Site Content (CMS)
// =============================================================================
const FIELD_GROUPS = [
  { title: "HERO (homepage top)", fields: [
    { key: "hero_label", label: "Small label above headline", type: "text" },
    { key: "hero_title", label: "Main headline", type: "text" },
    { key: "hero_title_highlight", label: "Highlighted trailing phrase (blue)", type: "text" },
    { key: "hero_subtitle", label: "Subtitle (below headline)", type: "textarea" },
    { key: "hero_image_url", label: "Hero background image URL", type: "image" },
  ]},
  { title: "MAP PREVIEW", fields: [
    { key: "map_preview_label", label: "Small label", type: "text" },
    { key: "map_preview_title", label: "Section heading", type: "text" },
  ]},
  { title: "WHY DRY ICE", fields: [
    { key: "why_label", label: "Small label", type: "text" },
    { key: "why_title", label: "Heading (periods separate lines)", type: "text" },
    { key: "why_title_highlight", label: "Highlighted trailing phrase", type: "text" },
    { key: "why_subtitle", label: "Paragraph", type: "textarea" },
  ]},
  { title: "USE CASES (feature card section)", fields: [
    { key: "use_cases_label", label: "Small label", type: "text" },
    { key: "use_cases_title", label: "Section heading", type: "text" },
  ]},
  { title: "VIDEO SECTION", fields: [
    { key: "video_label", label: "Small label", type: "text" },
    { key: "video_title", label: "Heading", type: "text" },
    { key: "video_title_highlight", label: "Highlighted trailing phrase", type: "text" },
    { key: "video_subtitle", label: "Paragraph", type: "textarea" },
    { key: "video_url", label: "YouTube or Vimeo URL (leave blank for placeholder)", type: "text" },
  ]},
  { title: "TESTIMONIALS SECTION HEADER", fields: [
    { key: "testimonials_label", label: "Small label", type: "text" },
    { key: "testimonials_title", label: "Section heading", type: "text" },
  ]},
  { title: "TRUST LOGOS STRIP HEADER", fields: [
    { key: "trust_logos_label", label: "Small label", type: "text" },
  ]},
  { title: "BOTTOM FOR-CONTRACTORS STRIP", fields: [
    { key: "bottom_label", label: "Small label", type: "text" },
    { key: "bottom_title", label: "Heading", type: "text" },
    { key: "bottom_title_highlight", label: "Highlighted trailing phrase", type: "text" },
    { key: "bottom_subtitle", label: "Paragraph", type: "textarea" },
  ]},
  { title: "FOOTER", fields: [
    { key: "footer_tagline", label: "Tagline (left column)", type: "textarea" },
    { key: "contact_email", label: "Contact email", type: "text" },
    { key: "contact_phone", label: "Contact phone", type: "text" },
    { key: "contact_address", label: "Contact address", type: "text" },
    { key: "social_facebook", label: "Facebook URL", type: "text" },
    { key: "social_instagram", label: "Instagram URL", type: "text" },
    { key: "social_linkedin", label: "LinkedIn URL", type: "text" },
    { key: "social_youtube", label: "YouTube URL", type: "text" },
    { key: "social_x", label: "X / Twitter URL", type: "text" },
    { key: "copyright_text", label: "Copyright line (use {YEAR} for current year)", type: "text" },
  ]},
];

function SiteContentTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const refreshContext = useRefreshSiteSettings();

  const load = async () => {
    try { const { data } = await api.get("/site-settings"); setSettings(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {};
      FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => { payload[f.key] = settings?.[f.key] ?? ""; }));
      await api.put("/admin/site-settings", payload);
      toast.success("Site content saved");
      refreshContext();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  if (!settings) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;

  return (
    <div className="space-y-6" data-testid="admin-site-content">
      <form onSubmit={save} className="space-y-6">
        {FIELD_GROUPS.map((group) => (
          <div key={group.title} className="surface p-6" data-testid={`sc-group-${group.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            <div className="section-label mb-4">— {group.title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={3} value={settings[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="input-rugged" data-testid={`sc-field-${f.key}`} />
                  ) : f.type === "image" ? (
                    <ImageUploadField value={settings[f.key] || ""} onChange={(v) => set(f.key, v)} testId={`sc-image-${f.key}`} />
                  ) : (
                    <input type="text" value={settings[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="input-rugged" data-testid={`sc-field-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="sticky bottom-4 flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="sc-save-btn">
            {saving ? "Saving..." : "Save All Site Content"}
          </button>
        </div>
      </form>

      <BlocksManager type="feature" title="FEATURE CARDS (homepage Use Cases)" subtitle="Icon + title + body. Appear in the Use Cases grid on the homepage." />
      <BlocksManager type="testimonial" title="TESTIMONIALS" subtitle="Quote + author + company. Shown in the Testimonials section." />
      <BlocksManager type="trust_logo" title="TRUST LOGOS" subtitle="Partner / press / certification logos shown under the hero." />
    </div>
  );
}

const ICON_OPTIONS = [
  { v: "car", l: "Car" }, { v: "factory", l: "Factory" }, { v: "wrench", l: "Wrench" },
  { v: "fire", l: "Fire" }, { v: "anchor", l: "Anchor" }, { v: "boat", l: "Boat" },
  { v: "building", l: "Building" }, { v: "flask", l: "Flask" }, { v: "drop", l: "Drop" },
  { v: "shield", l: "Shield" }, { v: "lightning", l: "Lightning" },
];

function BlocksManager({ type, title, subtitle }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const refreshContext = useRefreshSiteSettings();

  const load = async () => {
    try { const { data } = await api.get(`/admin/site-blocks?block_type=${type}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const newBlock = () => setEditing({
    block_type: type, order: (items[items.length - 1]?.order || 0) + 10, active: true,
    title: "", body: "", icon_key: "lightning", image_url: "", author: "", company: "", link_url: "",
  });

  const save = async (form) => {
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editing?.id) await api.put(`/admin/site-blocks/${editing.id}`, payload);
      else await api.post(`/admin/site-blocks`, payload);
      toast.success("Saved");
      setEditing(null); load(); refreshContext();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/site-blocks/${id}`); toast.success("Deleted"); load(); refreshContext(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) return <BlockForm type={type} initial={editing} onSave={save} onCancel={() => setEditing(null)} />;

  return (
    <div className="surface p-6" data-testid={`blocks-${type}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="section-label">— {title}</div>
          <div className="font-plex text-xs text-[#52525B] mt-1">{subtitle}</div>
        </div>
        <button onClick={newBlock} className="btn-primary text-xs" data-testid={`blocks-new-${type}`}>
          <Plus size={12} weight="bold" /> Add {type === "trust_logo" ? "logo" : type}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="font-mono text-xs text-[#52525B] py-4 text-center">No items yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => (
            <div key={b.id} className="bg-[#0A0A0A] border border-[#262626] p-3 flex items-center gap-3" data-testid={`block-${b.id}`}>
              <span className="font-mono text-[10px] text-[#52525B] w-10">#{b.order}</span>
              {b.image_url ? <img src={b.image_url} alt="" className="w-10 h-10 object-cover" /> : <div className="w-10 h-10 bg-[#121212] flex items-center justify-center font-mono text-[9px] text-[#52525B]">{b.icon_key || "—"}</div>}
              <div className="flex-1 min-w-0">
                <div className="font-chivo font-bold text-white truncate">{b.title || b.author || "Untitled"}</div>
                <div className="font-mono text-xs text-[#52525B] truncate">{b.body || b.company || b.link_url || ""}</div>
              </div>
              <span className={`tag ${b.active ? "tag-active" : ""}`}>{b.active ? "ACTIVE" : "HIDDEN"}</span>
              <button onClick={() => setEditing(b)} className="btn-secondary text-xs" data-testid={`block-edit-${b.id}`}><PencilSimple size={12} weight="bold" /></button>
              <button onClick={() => remove(b.id)} className="btn-secondary text-xs hover:border-[#FF5722]" data-testid={`block-delete-${b.id}`}><Trash size={12} weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockForm({ type, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => { e.preventDefault(); setSaving(true); await onSave(form); setSaving(false); };

  return (
    <form onSubmit={submit} className="surface p-6 space-y-4" data-testid={`block-form-${type}`}>
      <div className="section-label">— {type.toUpperCase()}</div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Display order</label>
          <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="input-rugged" data-testid="block-order" />
        </div>
        <label className="flex items-center gap-2 input-rugged cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="block-active" />
          <span className="font-plex text-sm">{form.active ? "Active" : "Hidden"}</span>
        </label>
      </div>

      {type === "feature" && (
        <>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Icon</label>
            <select value={form.icon_key} onChange={(e) => set("icon_key", e.target.value)} className="input-rugged" data-testid="block-icon">
              {ICON_OPTIONS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Title</label>
            <input required value={form.title} onChange={(e) => set("title", e.target.value)} className="input-rugged" data-testid="block-title" />
          </div>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Body</label>
            <textarea rows={3} required value={form.body} onChange={(e) => set("body", e.target.value)} className="input-rugged" data-testid="block-body" />
          </div>
        </>
      )}

      {type === "testimonial" && (
        <>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Quote</label>
            <textarea rows={4} required value={form.body} onChange={(e) => set("body", e.target.value)} className="input-rugged" data-testid="block-quote" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Author</label>
              <input required value={form.author} onChange={(e) => set("author", e.target.value)} className="input-rugged" data-testid="block-author" />
            </div>
            <div>
              <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Company</label>
              <input value={form.company} onChange={(e) => set("company", e.target.value)} className="input-rugged" data-testid="block-company" />
            </div>
          </div>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Avatar image (optional)</label>
            <ImageUploadField value={form.image_url} onChange={(v) => set("image_url", v)} testId="block-image" />
          </div>
        </>
      )}

      {type === "trust_logo" && (
        <>
          <div>
            <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Logo image</label>
            <ImageUploadField value={form.image_url} onChange={(v) => set("image_url", v)} testId="block-image" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Partner / press name (alt text)</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} className="input-rugged" data-testid="block-title" />
            </div>
            <div>
              <label className="block font-mono text-xs text-[#9CA3AF] uppercase tracking-widest mb-2">Link URL (optional)</label>
              <input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} className="input-rugged" data-testid="block-link-url" />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="block-save">{saving ? "Saving..." : "Save"}</button>
      </div>
    </form>
  );
}

